import { useEffect, useState } from "react";
import { LineChart, Mic, Sparkles, Flame, Globe2, Layers, AppWindow, Clock, BarChart3 } from "lucide-react";
import api from "@/lib/api";

const LANG_NAME = { en: "English", hi: "Hindi", te: "Telugu", mixed: "Mixed", "en-IN": "English (IN)", "hi-IN": "Hindi (IN)", "te-IN": "Telugu (IN)" };
const MODE_LABEL = { raw: "Raw Text", auto: "Auto", clean: "Grammar Fix", smart_english: "Smart English", email: "Email", whatsapp: "WhatsApp", prompt: "AI Prompt", client_req: "Client Brief", blog_outline: "Blog Outline", meeting_note: "Meeting Notes" };

function StatCard({ icon: Icon, label, value, sub, gradient }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl p-5 border ${gradient ? "bg-gradient-primary text-white border-transparent shadow-glow-purple" : "bg-white border-brand-borderc"}`}>
      {gradient && <div className="absolute inset-0 bg-mesh opacity-30 pointer-events-none" />}
      <div className="relative flex items-start justify-between">
        <div>
          <div className={`text-[11px] uppercase tracking-widest font-semibold ${gradient ? "text-white/80" : "text-brand-muted"}`}>{label}</div>
          <div className="mt-2 text-3xl font-bold">{value}</div>
          {sub && <div className={`mt-1 text-xs ${gradient ? "text-white/80" : "text-brand-muted"}`}>{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${gradient ? "bg-white/15" : "bg-brand-surface text-brand-purple"}`}>
          <Icon className={`w-5 h-5 ${gradient ? "text-white" : ""}`} />
        </div>
      </div>
    </div>
  );
}

function BarRow({ label, count, total, color = "bg-gradient-primary" }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-brand-text">
        <span className="font-medium">{label}</span>
        <span className="text-brand-muted">{count} · {pct}%</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-brand-surface overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Heatmap({ data }) {
  // 30-day vertical heatmap as a 6 cols × 5 rows grid
  const max = Math.max(1, ...data.map((d) => d.count));
  const level = (c) => {
    if (c === 0) return "bg-brand-surface";
    const r = c / max;
    if (r > 0.75) return "bg-gradient-primary";
    if (r > 0.5) return "bg-brand-purple/70";
    if (r > 0.25) return "bg-brand-purple/40";
    return "bg-brand-purple/20";
  };
  return (
    <div className="grid grid-cols-10 gap-1.5">
      {data.map((d) => (
        <div
          key={d.date}
          title={`${d.date}: ${d.count} dictation${d.count === 1 ? "" : "s"}`}
          className={`aspect-square rounded ${level(d.count)} transition`}
        />
      ))}
    </div>
  );
}

export default function InsightsPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  const load = async (d = days) => {
    setData(null);
    const r = await api.get(`/insights?days=${d}`);
    setData(r.data);
  };
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  if (!data) return <div className="p-10 text-brand-muted">Loading insights…</div>;

  const isEmpty = data.total_dictations === 0;
  const isLowData = data.total_dictations > 0 && data.total_dictations < 5;
  const langTotal = data.language_usage.reduce((s, x) => s + x.count, 0);
  const modeTotal = data.mode_usage.reduce((s, x) => s + x.count, 0);
  const srcTotal = data.source_usage.reduce((s, x) => s + x.count, 0);

  return (
    <div className="px-5 lg:px-10 py-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy">Insights</h1>
          <p className="text-sm text-brand-muted mt-1">Your voice writing — at a glance.</p>
        </div>
        <div className="inline-flex items-center gap-1 bg-white border border-brand-borderc rounded-full p-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d} onClick={() => setDays(d)} data-testid={`insights-range-${d}`}
              className={`px-3 py-1 rounded-full text-xs font-medium ${days === d ? "bg-gradient-primary text-white" : "text-brand-muted"}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Mic} label="Dictations" value={data.total_dictations.toLocaleString()} sub={`Best day: ${data.best_day?.count || 0}`} gradient />
        <StatCard icon={BarChart3} label="Words spoken" value={data.total_raw_words.toLocaleString()} sub={`${data.total_polished_words.toLocaleString()} polished`} />
        <StatCard icon={Sparkles} label="AI fixes made" value={data.fixes_made.toLocaleString()} sub={`Avg ~${data.total_dictations ? Math.round(data.fixes_made / data.total_dictations) : 0} per draft`} />
        <StatCard icon={Flame} label="Streak" value={`${data.streak_days} day${data.streak_days === 1 ? "" : "s"}`} sub={data.streak_days > 0 ? "Keep it going" : "Start your streak today"} />
        <StatCard icon={Clock} label="Approx WPM" value={data.avg_wpm || "—"} sub="Avg speaking pace" />
        <StatCard icon={Layers} label="Top style" value={MODE_LABEL[data.most_used_mode] || data.most_used_mode} sub="Most used output mode" />
        <StatCard icon={Globe2} label="Languages" value={data.language_usage.length} sub={data.language_usage.slice(0, 2).map((l) => LANG_NAME[l.code] || l.code).join(" · ") || "—"} />
        <StatCard icon={AppWindow} label="Sources" value={data.source_usage.length} sub={data.source_usage.slice(0, 2).map((s) => s.source).join(" · ") || "—"} />
      </div>

      {isEmpty ? (
        <div className="mt-8 bg-white rounded-3xl border border-brand-borderc p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-primary mx-auto flex items-center justify-center text-white shadow-glow-purple">
            <LineChart className="w-7 h-7" />
          </div>
          <h3 className="mt-5 text-xl font-semibold text-brand-navy">No data yet — speak your first draft</h3>
          <p className="mt-2 text-sm text-brand-muted max-w-md mx-auto">Once you start dictating, this page will show your words spoken, language mix, output styles, daily streak and more.</p>
          <a href="/app" className="mt-6 inline-flex items-center gap-2 btn-primary-gradient rounded-full px-5 py-2.5 text-sm font-semibold">
            Open Voice Studio <Mic className="w-4 h-4" />
          </a>
        </div>
      ) : isLowData ? (
        <div className="mt-6 grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-3 relative overflow-hidden rounded-3xl bg-gradient-primary text-white p-7">
            <div className="absolute inset-0 bg-mesh opacity-30 pointer-events-none" />
            <div className="relative grid md:grid-cols-2 gap-6 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 rounded-full text-[11px] font-semibold backdrop-blur">
                  <Sparkles className="w-3.5 h-3.5" /> Just getting started
                </div>
                <h3 className="mt-4 text-2xl font-bold">
                  {5 - data.total_dictations} more drafts to unlock your full insights.
                </h3>
                <p className="mt-2 text-sm text-white/85">
                  We need at least 5 dictations to compute WPM, streaks and your daily activity heatmap reliably.
                </p>
                <a href="/app" className="mt-5 inline-flex items-center gap-2 bg-white text-brand-navy rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-white/90 transition">
                  <Mic className="w-4 h-4" /> Open Voice Studio
                </a>
              </div>
              <div className="bg-white/10 rounded-2xl p-5 backdrop-blur">
                <div className="text-[11px] uppercase tracking-widest opacity-80">Your progress</div>
                <div className="mt-2 flex items-end justify-between">
                  <div className="text-3xl font-bold">{data.total_dictations} / 5</div>
                  <div className="text-xs opacity-80">{Math.round((data.total_dictations / 5) * 100)}%</div>
                </div>
                <div className="mt-2 h-2 rounded-full bg-white/20 overflow-hidden">
                  <div className="h-full bg-white rounded-full" style={{ width: `${(data.total_dictations / 5) * 100}%` }} />
                </div>
                <div className="mt-3 text-xs opacity-85">
                  {data.total_raw_words} words spoken so far
                </div>
              </div>
            </div>
          </div>

          <StatCard icon={Mic} label="Sessions" value={data.total_dictations} sub="Total recordings" />
          <StatCard icon={BarChart3} label="Words" value={data.total_raw_words.toLocaleString()} sub="Spoken" />
          <StatCard icon={Globe2} label="Language" value={data.language_usage[0]?.code ? (LANG_NAME[data.language_usage[0].code] || data.language_usage[0].code) : "—"} sub="Most spoken" />
          <StatCard icon={AppWindow} label="Source" value={data.source_usage[0]?.source || "—"} sub="Most used" />
          <StatCard icon={Flame} label="Streak" value="Coming up" sub="Reach 5 drafts to start" />
          <StatCard icon={Clock} label="WPM" value="Soon" sub="Calculated from 5+ drafts" />

          <div className="lg:col-span-3 bg-white rounded-2xl border border-brand-borderc p-5">
            <h3 className="font-semibold text-brand-navy">What you'll unlock</h3>
            <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
              {[
                ["Daily heatmap", "See when you draft the most"],
                ["Streak tracking", "Build a daily voice-writing habit"],
                ["Approx WPM", "Track your speaking pace"],
                ["Top output style", "Email vs WhatsApp vs Prompt"],
                ["Language mix", "Auto-detected per recording"],
                ["Source app usage", "Web · Extension · Desktop"],
              ].map(([title, sub]) => (
                <div key={title} className="p-3 rounded-xl bg-brand-surface">
                  <div className="font-semibold text-brand-navy">{title}</div>
                  <div className="text-xs text-brand-muted mt-0.5">{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-6 grid lg:grid-cols-3 gap-5">
          {/* Heatmap */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-brand-borderc p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-brand-navy">Activity · last {days} days</h3>
                <p className="text-xs text-brand-muted mt-0.5">Daily dictation volume — darker = busier.</p>
              </div>
              <span className="text-xs text-brand-muted">Best: <strong className="text-brand-navy">{data.best_day?.date}</strong> · {data.best_day?.count} drafts</span>
            </div>
            <div className="mt-4">
              <Heatmap data={data.heatmap} />
            </div>
            <div className="mt-3 flex items-center gap-2 text-[10px] text-brand-muted">
              <span>Less</span>
              <span className="w-3 h-3 rounded bg-brand-surface" />
              <span className="w-3 h-3 rounded bg-brand-purple/20" />
              <span className="w-3 h-3 rounded bg-brand-purple/40" />
              <span className="w-3 h-3 rounded bg-brand-purple/70" />
              <span className="w-3 h-3 rounded bg-gradient-primary" />
              <span>More</span>
            </div>
          </div>

          {/* Languages */}
          <div className="bg-white rounded-2xl border border-brand-borderc p-5">
            <h3 className="font-semibold text-brand-navy">Language mix</h3>
            <p className="text-xs text-brand-muted mt-0.5">Detected from your voice.</p>
            <div className="mt-4 space-y-3">
              {data.language_usage.length === 0 ? (
                <div className="text-sm text-brand-muted">No data yet.</div>
              ) : data.language_usage.map((l) => (
                <BarRow key={l.code} label={LANG_NAME[l.code] || l.code} count={l.count} total={langTotal} />
              ))}
            </div>
          </div>

          {/* Modes */}
          <div className="bg-white rounded-2xl border border-brand-borderc p-5">
            <h3 className="font-semibold text-brand-navy">Output styles</h3>
            <p className="text-xs text-brand-muted mt-0.5">Which polish did you reach for most.</p>
            <div className="mt-4 space-y-3">
              {data.mode_usage.length === 0 ? (
                <div className="text-sm text-brand-muted">No data yet.</div>
              ) : data.mode_usage.map((m) => (
                <BarRow key={m.mode} label={MODE_LABEL[m.mode] || m.mode} count={m.count} total={modeTotal} color="bg-gradient-secondary" />
              ))}
            </div>
          </div>

          {/* Sources */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-brand-borderc p-5">
            <h3 className="font-semibold text-brand-navy">Where you dictate</h3>
            <p className="text-xs text-brand-muted mt-0.5">Web app + Chrome extension + any other source.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {data.source_usage.length === 0 ? (
                <div className="text-sm text-brand-muted">No data yet.</div>
              ) : data.source_usage.map((s) => (
                <BarRow key={s.source} label={s.source} count={s.count} total={srcTotal} color="bg-gradient-warm" />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
